import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { UserProfile } from '../types';
import toast from 'react-hot-toast';
import { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  signUp: (email: string, password: string, username: string, full_name: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  loading: boolean;
  updateProfile: (profile: Partial<UserProfile>) => Promise<UserProfile>;
  deleteAccount: () => Promise<void>;
  isAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar sessão atual
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      }
    }).catch(error => {
      console.error('Erro ao verificar a sessão:', error);
      setLoading(false);
    });

    // Escutar mudanças na autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setUserProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (error) {
      console.error('Erro ao buscar perfil:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    
    // Após login bem-sucedido, obter o perfil do usuário para redirecionamento
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (currentUser) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', currentUser.id)
        .single();
      
      if (profileData?.username) {
        navigate(`/perfil/${profileData.username}`);
      }
    }
  };

  const signUp = async (email: string, password: string, username: string, full_name: string) => {
    // Verificar se o nome de usuário contém espaços
    if (username.includes(' ')) {
      throw new Error('O nome de usuário não pode conter espaços');
    }

    // Verificar se o nome de usuário já existe
    const { data: existingUser, error: checkError } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', username)
      .single();

    if (existingUser) {
      throw new Error('Este nome de usuário já está em uso');
    }

    if (checkError && checkError.code !== 'PGRST116') {
      // PGRST116 é o código para 'não encontrado', que é o que queremos
      throw checkError;
    }

    const { data: { user }, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          full_name
        }
      }
    });

    if (error) throw error;
    if (!user) throw new Error('Usuário não criado');

    // Criar perfil do usuário
    const { error: profileError } = await supabase.from('profiles').insert({
      id: user.id,
      username,
      full_name,
      email: user.email,
    });

    if (profileError) throw profileError;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    
    // Após logout bem-sucedido, redirecionar para a página inicial
    navigate('/');
  };

  const updateProfile = async (profile: Partial<UserProfile>) => {
    if (!user) throw new Error('Usuário não autenticado');
    if (!userProfile) throw new Error('Perfil não encontrado');

    try {
      // Verificar se está tentando atualizar o nome de usuário
      if (profile.username) {
        // Verificar se o nome de usuário contém espaços
        if (profile.username.includes(' ')) {
          throw new Error('O nome de usuário não pode conter espaços');
        }

        // Verificar se o nome de usuário já existe (se for diferente do atual)
        if (profile.username !== userProfile.username) {
          // Verificar se passaram 30 dias desde a última alteração de nome de usuário
          if (userProfile.last_username_change) {
            const lastChange = new Date(userProfile.last_username_change);
            const now = new Date();
            const diffDays = Math.floor((now.getTime() - lastChange.getTime()) / (1000 * 60 * 60 * 24));
            
            if (diffDays < 30) {
              const daysRemaining = 30 - diffDays;
              throw new Error(`Você só pode alterar seu nome de usuário a cada 30 dias. Aguarde mais ${daysRemaining} dias.`);
            }
          }

          const { data: existingUser, error: checkError } = await supabase
            .from('profiles')
            .select('username')
            .eq('username', profile.username)
            .single();

          if (existingUser) {
            throw new Error('Este nome de usuário já está em uso');
          }

          if (checkError && checkError.code !== 'PGRST116') {
            // PGRST116 é o código para 'não encontrado', que é o que queremos
            throw checkError;
          }
        }
      }

      // Remover campos undefined ou null
      const cleanProfile = Object.fromEntries(
        Object.entries(profile).filter(([_, value]) => value !== undefined && value !== null)
      );

      // Garantir que campos obrigatórios estejam presentes
      const dataToUpdate = {
        ...cleanProfile,
        id: user.id,
        updated_at: new Date().toISOString()
      };
      
      // Se estiver atualizando o nome de usuário, atualizar a data da última alteração
      if (profile.username && profile.username !== userProfile.username) {
        dataToUpdate.last_username_change = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(dataToUpdate)
        .eq('id', user.id)
        .select('*')
        .single();

      if (error) {
        console.error('Erro Supabase:', error);
        throw new Error(error.message);
      }

      if (!data) {
        throw new Error('Nenhum dado retornado após atualização');
      }

      const updatedProfile = data as UserProfile;
      setUserProfile(updatedProfile);
      return updatedProfile;
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      throw error;
    }
  };

  const deleteAccount = async () => {
    if (!user) throw new Error('Usuário não autenticado');
    
    try {
      setLoading(true);
      
      // Armazenar o ID do usuário antes de iniciar o processo de exclusão
      const userId = user.id;
      
      // 1. Remover avatar do storage se existir
      if (userProfile?.avatar_url) {
        try {
          const avatarPath = userProfile.avatar_url.split('/').pop();
          if (avatarPath) {
            await supabase.storage
              .from('avatars')
              .remove([`${userId}/${avatarPath}`]);
          }
        } catch (error) {
          console.error('Erro ao remover avatar:', error);
        }
      }
      
      // 2. Remover posts do usuário
      const { error: postsError } = await supabase
        .from('posts')
        .delete()
        .eq('user_id', userId);
        
      if (postsError) {
        console.error('Erro ao remover posts:', postsError);
      }
      
      // 3. Remover likes do usuário
      const { error: likesError } = await supabase
        .from('post_likes')
        .delete()
        .eq('user_id', userId);
        
      if (likesError) {
        console.error('Erro ao remover likes:', likesError);
      }
      
      // 4. Remover amizades do usuário
      const { error: friendshipsError1 } = await supabase
        .from('friendships')
        .delete()
        .eq('requester_id', userId);
        
      if (friendshipsError1) {
        console.error('Erro ao remover amizades (requester):', friendshipsError1);
      }
      
      const { error: friendshipsError2 } = await supabase
        .from('friendships')
        .delete()
        .eq('addressee_id', userId);
        
      if (friendshipsError2) {
        console.error('Erro ao remover amizades (addressee):', friendshipsError2);
      }
      
      // 5. Remover perfil do usuário
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);
        
      if (profileError) {
        console.error('Erro ao remover perfil:', profileError);
        throw new Error('Erro ao remover perfil do usuário');
      }
      
      // 6. Obter o token de acesso atual para a função Edge
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Sessão não encontrada');
      }
      
      // 7. Fazer logout para encerrar a sessão atual
      await signOut();
      
      // 8. Chamar a função Edge para excluir o usuário no Auth
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        if (!supabaseUrl) {
          throw new Error('URL do Supabase não encontrada');
        }
        
        console.log('Chamando função Edge para excluir usuário:', userId);
        
        const response = await fetch(`${supabaseUrl}/functions/v1/delete-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        
        const responseText = await response.text();
        console.log('Resposta da função Edge (texto):', responseText);
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Erro ao analisar resposta JSON:', e);
          data = { success: false, error: 'Erro ao analisar resposta' };
        }
        
        console.log('Resposta da função Edge (JSON):', data);
        
        if (!response.ok || !data.success) {
          throw new Error(data.error || `Erro ao excluir conta de autenticação: ${response.status}`);
        }
        
        // Sucesso na exclusão
        toast.success('Sua conta foi excluída com sucesso');
      } catch (error) {
        console.error('Erro ao chamar função de exclusão:', error);
        toast.error('Seus dados foram removidos, mas houve um problema ao excluir sua conta de autenticação. Por favor, entre em contato com o suporte.');
      }
      
      // 9. Redirecionar para a página inicial
      navigate('/');
      
    } catch (error) {
      console.error('Erro ao excluir conta:', error);
      setLoading(false);
      throw error;
    }
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  const isAdmin = () => {
    return userProfile?.role === 'ADMIN';
  };

  const value = {
    user,
    userProfile,
    signUp,
    signIn,
    signOut,
    loading,
    updateProfile,
    deleteAccount,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}